import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, FileText, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import useSeo from '../hooks/useSeo';
import { IMAGES, CONTACT_INFO, CONTACT_REASONS, CONTACT_SECTORS, OPPORTUNITIES, VALUE_FOOTER } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: '', email: '', reason: '', sector: '', phone: '', message: '', agree: false };

const Contact = () => {
  useSeo({
    title: 'Contact Duffy Investment Group | Surfers Paradise',
    description: 'Contact Duffy Investment Group in Surfers Paradise, Queensland regarding investment opportunities, partnerships, proposals and general business enquiries.',
    keywords: 'contact Duffy Investment Group, Duffy Investment Group Surfers Paradise, investment enquiries, investment opportunities, business proposals, partnerships',
  });

  const [searchParams] = useSearchParams();
  const preset = searchParams.get('sector');
  const [form, setForm] = useState({
    ...initial,
    sector: CONTACT_SECTORS.includes(preset) ? preset : '',
  });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.name || !form.email || !form.reason || !form.message) {
      toast.error('Please complete all required fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (!form.agree) {
      toast.error('Please agree to the Privacy Policy to continue.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        reason: form.reason,
        sector: form.sector,
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      toast.success("Thank you — your message has been sent. We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Sorry, something went wrong sending your message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* ===================== HERO ===================== */}
      <section className="relative bg-navy-grad overflow-hidden">
        <div className="absolute inset-0 lg:left-[45%]">
          <img src={IMAGES.reception} alt="DIG office reception" className="w-full h-full object-cover" />
          <div className="absolute inset-0 lg:hidden" style={{ background: 'linear-gradient(180deg, rgba(7,19,34,0.4) 0%, rgba(7,19,34,0.65) 45%, rgba(7,19,34,0.95) 100%)' }} />
          <div className="absolute inset-0 hidden lg:block" style={{ background: 'linear-gradient(90deg, rgba(10,26,48,1) 0%, rgba(10,26,48,0.7) 18%, rgba(10,26,48,0.1) 45%)' }} />
        </div>
        <div className="relative dig-container">
          <div className="flex items-center" style={{ minHeight: 360 }}>
            <div className="pt-10 pb-14 max-w-lg">
              <Reveal>
                <h1 className="font-serif-dig text-white" style={{ fontSize: 'clamp(1.7rem, 5.6vw, 3rem)', fontWeight: 700, lineHeight: 1.1 }}>
                  LET'S START A CONVERSATION
                </h1>
                <span className="gold-rule-lg block my-6" />
                <p className="font-serif-dig gold-text" style={{ fontSize: '1.3rem', fontWeight: 600, lineHeight: 1.4 }}>
                  We're here to listen and explore opportunities.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== INFO + FORM ===================== */}
      <section className="bg-cream">
        <div className="dig-container py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-14">
            {/* Get in touch */}
            <Reveal>
              <h2 className="section-title" style={{ fontSize: '1.7rem' }}>GET IN TOUCH</h2>
              <span className="gold-rule block mt-4 mb-8" />
              <ul className="space-y-6">
                {[
                  { icon: MapPin, label: 'Office', lines: CONTACT_INFO.office },
                  { icon: MapPin, label: 'Postal Address', lines: CONTACT_INFO.postal },
                  { icon: FileText, label: CONTACT_INFO.abn, lines: [CONTACT_INFO.acn] },
                ].map((row, i) => {
                  const RowIcon = row.icon;
                  return (
                    <li key={i} className="flex gap-4">
                      <RowIcon size={24} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <p className="font-body navy-text" style={{ fontWeight: 700 }}>{row.label}</p>
                        {row.lines.map((l, j) => (
                          <p key={j} className="font-body" style={{ color: 'var(--muted)', lineHeight: 1.5 }}>{l}</p>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Reveal>

            {/* Form */}
            <Reveal delay={120}>
              <h2 className="section-title" style={{ fontSize: '1.7rem' }}>HOW CAN WE HELP YOU?</h2>
              <p className="font-body mt-4" style={{ color: 'var(--muted)', lineHeight: 1.7 }}>
                Whether you have an investment opportunity, a business proposal or simply want to learn more about DIG, we'd love to hear from you.
              </p>
              <form onSubmit={submit} className="mt-7 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input placeholder="Full Name *" value={form.name} onChange={(e) => set('name', e.target.value)} className="bg-white h-12" />
                  <Input placeholder="Email Address *" value={form.email} onChange={(e) => set('email', e.target.value)} className="bg-white h-12" />
                </div>
                <Select value={form.reason} onValueChange={(v) => set('reason', v)}>
                  <SelectTrigger className="bg-white h-12 text-left" style={{ color: form.reason ? 'var(--ink)' : '#8a94a6' }}>
                    <SelectValue placeholder="What is this regarding? *" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.sector} onValueChange={(v) => set('sector', v)}>
                  <SelectTrigger data-testid="contact-sector-select" className="bg-white h-12 text-left" style={{ color: form.sector ? 'var(--ink)' : '#8a94a6' }}>
                    <SelectValue placeholder="Which investment sector?" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Phone Number" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="bg-white h-12" />
                <Textarea placeholder="Message *" value={form.message} onChange={(e) => set('message', e.target.value)} className="bg-white" rows={5} />
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.agree} onCheckedChange={(v) => set('agree', !!v)} className="mt-1" />
                  <span className="font-body" style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    I agree to the <span className="gold-text" style={{ textDecoration: 'underline' }}>  <a href="/images/privacy-dig.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a></span> and consent to DIG collecting my details.
                  </span>
                </label>
                <button type="submit" className="btn-gold" disabled={sending}>
                  {sending ? (<><Loader2 size={16} className="animate-spin" /> SENDING…</>) : (<>SEND MESSAGE <ArrowRight size={16} /></>)}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== OPPORTUNITIES ===================== */}
      <section className="bg-navy">
        <div className="dig-container py-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="font-serif-dig gold-text" style={{ fontSize: '1.4rem', fontWeight: 700 }}>OPPORTUNITIES WE CONSIDER</h3>
              <ul className="mt-6 space-y-3">
                {OPPORTUNITIES.map((o) => (
                  <li key={o} className="flex items-center gap-3">
                    <CheckCircle2 size={20} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                    <span className="font-body" style={{ color: 'rgba(255,255,255,0.9)' }}>{o}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(200,162,76,0.3)' }} className="p-8 flex gap-5 items-start">
              <Icon name="Globe" size={54} strokeWidth={1} style={{ color: 'var(--gold)', flexShrink: 0 }} />
              <div>
                <h4 className="font-serif-dig gold-text" style={{ fontSize: '1.2rem', fontWeight: 700, lineHeight: 1.3 }}>GLOBAL VISION. LOCAL KNOWLEDGE. STRONG PARTNERSHIPS.</h4>
                <p className="font-body mt-3" style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}>
                  Partner with DIG to build, grow and create lasting value.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== VALUE FOOTER ===================== */}
      <section className="bg-cream-2">
        <div className="dig-container py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {VALUE_FOOTER.map((v) => (
              <div key={v.label} className="flex flex-col items-center text-center gap-2">
                <Icon name={v.icon} size={34} strokeWidth={1.3} style={{ color: 'var(--gold)' }} />
                <span className="font-body navy-text" style={{ fontWeight: 600, fontSize: '0.95rem' }}>{v.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
